/**
 * <cw-slot>: "When should it arrive?" (design-handoff/components/slot-picker.md, Shopping v13).
 * Opens on 'cw:slot:open' (the header's slot line, Home's slot strip).
 *
 * The rows are Flow's answer for the chosen address, never the prototype's mock slots:
 * - STANDARD DELIVERY: Flow times standard delivery by the clock, so there is one row, the
 *   window an order placed now gets ("Today · before 6pm"), with its cut-off and the fee for
 *   this basket ("₹90 delivery, free over ₹799"). Beyond the delivery edge it reads "Not
 *   available at this address".
 * - EXPRESS: only when Flow offers it (switched off today), with its price and how much more
 *   makes it free.
 * - PICK UP: beyond the edge, "Pick up at our shop", ready when Flow says (v13 1j).
 * Picking a row closes the sheet and the header follows; checkout's own rate list confirms it.
 * With no address yet, the time-of-day promise shows and the fee waits for the address.
 */
import { CwSheet } from '@cw/sheet';
import { view, slotChoice, chooseSlot } from '@cw/delivery';

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const rupees = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : '');
/** "Today before 6pm" → "Today · before 6pm" (the design's "Today · 1–3 pm"). */
const slotWords = (when) => String(when || '').replace(/^(Today|Tomorrow)\s+/i, '$1 · ');

class CwSlot extends CwSheet {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('cw:slot:open', this.#onRequest);
    document.addEventListener('cw:delivery:change', this.#onChange);
    this.addEventListener('click', this.#onPick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cw:slot:open', this.#onRequest);
    document.removeEventListener('cw:delivery:change', this.#onChange);
  }

  #onRequest = (event) => {
    event.preventDefault();
    this.#paint();
    this.open(event.detail?.source);
  };

  #onChange = () => {
    if (this.isOpen) this.#paint();
  };

  #row({ kind, a, b, price = '', on = false, off = false }) {
    return `
      <button type="button" class="cw-slot__row${on ? ' is-on' : ''}${off ? ' is-off' : ''}" data-kind="${kind}"${off ? ' disabled' : ''} aria-pressed="${on}">
        <span class="cw-slot__dot" aria-hidden="true"></span>
        <span class="cw-slot__text">
          <span class="cw-slot__a">${esc(a)}</span>
          ${b ? `<span class="cw-slot__b">${esc(b)}</span>` : ''}
        </span>
        ${price ? `<span class="cw-slot__price">${esc(price)}</span>` : ''}
      </button>`;
  }

  #paint() {
    const v = view();
    const a = v.answer || null;
    const chosen = slotChoice();
    const parts = [];
    const head = (words) => `<p class="cw-slot__eyebrow">${words}</p>`;
    const far = !!a && a.spot !== 'origin' && a.serviceable === false;
    const s = a?.standard || {};

    // Standard: one window, the one an order placed now gets.
    if (s.when || !a || far) {
      const cut = s.order_by ? `Order by ${s.order_by}${s.order_by_tomorrow ? ' tomorrow' : ''}` : '';
      let fee = '';
      if (a && a.spot !== 'origin' && !far) fee = s.free ? 'Free delivery' : `${rupees(s.price)} delivery, free over ${rupees(a.free_over)}`;
      if (!a || a.spot === 'origin') fee = 'Set your location for the fee';
      const words = s.when ? slotWords(s.when) : 'Standard delivery';
      parts.push(
        `<div class="cw-slot__group">${head('STANDARD DELIVERY')}${this.#row({
          kind: 'standard',
          a: words,
          b: far ? 'Not available at this address' : [cut, fee].filter(Boolean).join(' · '),
          on: !far && (chosen !== 'express' || !a?.express),
          off: far,
        })}</div>`
      );
    }

    // Express: only when Flow offers it here.
    if (a && a.express && !far) {
      const e = a.express;
      const left = Math.max(0, Number(e.free_over || 0) - Number(a.value || 0));
      parts.push(
        `<div class="cw-slot__group">${head('EXPRESS')}${this.#row({
          kind: 'express',
          a: 'Express',
          b: e.when ? `Arrives ${lowerFirst(e.when)}` : '',
          price: e.free ? 'Free' : rupees(e.price),
          on: chosen === 'express',
        })}<p class="cw-slot__note">${e.free ? 'Free express unlocked ✓' : `Free over ${rupees(e.free_over)} · ${rupees(left)} to go`}</p></div>`
      );
    }

    // Beyond the edge: pick up at the shop.
    if (far) {
      parts.push(
        `<div class="cw-slot__group">${head('PICK UP')}${this.#row({
          kind: 'pickup',
          a: 'Pick up at our shop',
          b: a.pickup?.when ? `Ready ${lowerFirst(a.pickup.when)} · Electronic City` : 'Electronic City',
          price: 'Free',
          on: true,
        })}</div>`
      );
    }

    if (!a || a.spot === 'origin') {
      parts.push('<button type="button" class="cw-slot__setloc" data-cw="setloc">Set your location to see delivery fees</button>');
    }
    this.querySelector('[data-cw="rows"]').innerHTML = parts.join('');
  }

  #onPick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-cw="setloc"]')) {
      this.close();
      document.dispatchEvent(new CustomEvent('cw:address:open', { cancelable: true, detail: { source: target } }));
      return;
    }
    const row = target.closest('.cw-slot__row');
    if (!row || row.disabled) return;
    const kind = row.dataset.kind;
    const words = [row.querySelector('.cw-slot__a')?.textContent, row.querySelector('.cw-slot__b')?.textContent].filter(Boolean).join(' · ');
    if (kind === 'standard' || kind === 'express') chooseSlot(kind, `${kind === 'express' ? 'Express' : 'Standard'} · ${words}`);
    this.close();
  };
}

if (!customElements.get('cw-slot')) customElements.define('cw-slot', CwSlot);
